import { api } from '@xtnd/web/shared/api';

/**
 * Response interface for tenant templates
 */
export interface TenantTemplatesResponse {
  templates: Array<{
    templateId: string;
    templateName: string;
    templateType: 'pdf' | 'email' | 'document';
    version: string;
    isActive: boolean;
    description?: string;
    tags?: string[] | string;
    variables?: Record<string, any>;
    fileSize: number;
    createdAt: string;
    createdBy: string;
    tenantId?: string;
    hasTemplateContent?: boolean;
    sampleData?: any;
  }>;
  total: number;
}

/**
 * Response interface for template upload
 */
export interface TemplateUploadResponse {
  templateId: string;
  templateName: string;
  message: string;
}

/**
 * Response interface for template deletion
 */
export interface TemplateDeleteResponse {
  message: string;
  juniperReset?: boolean;
}

/**
 * API service for tenant template CRUD operations
 */
class TemplateManagementApi {
  /**
   * Get list of tenant templates
   * @param type - Optional filter by template type (pdf, email, document)
   * @returns Promise with tenant templates
   */
  async getTenantTemplates(type?: string): Promise<TenantTemplatesResponse> {
    const params = type && type !== 'all' ? `?type=${type}` : '';
    const endpoint = `/templates/list${params}`;
    return api.get<TenantTemplatesResponse>(endpoint);
  }

  /**
   * Upload a new template
   * @param formData - FormData containing template file and metadata
   * @returns Promise with upload result
   */
  async uploadTemplate(formData: FormData): Promise<TemplateUploadResponse> {
    return api.post<TemplateUploadResponse>('/templates/upload', formData);
  }

  /**
   * Delete a template
   * @param templateId - ID of template to delete
   * @returns Promise with deletion result
   */
  async deleteTemplate(templateId: string): Promise<TemplateDeleteResponse> {
    return api.delete<TemplateDeleteResponse>(`/templates/${templateId}`);
  }

  /**
   * Get download URL for template .hbs file
   * @param templateId - ID of template to download
   * @returns Download URL
   */
  getDownloadUrl(templateId: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
    return `${baseUrl}/templates/${templateId}/download`;
  }
}

export const templateManagementApi = new TemplateManagementApi();

